import { darken, lighten, rem } from "polished"

const global_font_size_px = 16

const _base_pixel_sizes = Array.from(
  new Set([10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 96, ...[global_font_size_px]]),
)

const size_pix_to_rem = {}
_base_pixel_sizes.forEach((size) => (size_pix_to_rem[size] = rem(size, global_font_size_px)))

const fullWhite = "#FFFFFF"
const black = "#000000"
const customRed = "#FF0000"
const brandRed = "rgba(255,0,0,0)"
// const customGreen = "#00FF00"
// const customBlue = "#0000FF"
const lightBlue = "#007bff"
// const gray = "#3c3c3c"
// const gray2 = "#aaaaaa"
// const gray3 = "#9d9c9c"
const gray4 = "#3f3f3f"
const gray5 = "#dddddd"
const gray6 = "#3c3c3b"
const gray7 = "#e3e3e3"
const gray8 = "#c6c6c5"
const gray9 = "#777777"
const gray10 = "#6c757d"
const alertRed = "rgb(255,40,40)"
const successGreen = "#48aa43"
const yellow = "#ffc107"

const variants = Object.freeze({
  brand: "brand",
  standard: "standard",
  highlight: "highlight",
  alert: "alert",
  success: "success",
  selectOn: "selectOn",
  selectOff: "selectOff",
  primary: "primary",
  secondary: "secondary",
  ghost: "ghost",
  danger: "danger",
  warning: "warning",
  disabled: "disabled",
  dimmed: "dimmed",
})

export const theme = {
  /**
   * spaces is paddings and margins, how one thing distance from each other
   * how we make the design breath
   */
  spaces: {
    tiny: "0.125rem",
    xtrasmall: "0.25rem",
    small: "0.5rem",
    smallToNormal: "0.75rem",
    normal: "1rem",
    medium: "2rem",
  },
  /**
   * Sizes might look like spaces but they are not.
   * They are fixed dimensions of things such as the border, icons, maxWidths and so on
   */
  sizes: {
    small: "0.5rem",
    normal: "1rem",
    normalToMedium: "1.5rem",
    medium: "2rem",
    large: "4rem",
    larger: "5rem",
  },
  fonts: {
    weight: {
      lighter: "100",
      light: "200",
      normal: "400",
      semiBold: "500",
      bold: "600",
      extraBold: "700",
    },
    size: {
      small: "0.7rem",
      normal: "1rem",

      heading1: size_pix_to_rem[48],
      heading2: size_pix_to_rem[36],
      heading3: size_pix_to_rem[28],
      heading4: size_pix_to_rem[24],
      heading5: size_pix_to_rem[18],
      heading6: size_pix_to_rem[14],
    },
    lineHeight: {
      quater: 0.25,
      half: 0.5,
      halfNSpace: 0.75,
      single: 1,
      singleNSpace: 1.25,
      oneAndHalf: 1.5,
      double: 2,
    },
    family: {
      primary: '"Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
      secondary: '"Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
    },
  },
  colors: {
    bg: fullWhite,
    fg: gray4,
    disabled: {
      fg: lighten(0.5, gray4),
      bg: gray5,
      border: gray5,
    },
    [variants.brand]: {
      fg: customRed,
      bg: brandRed,
      border: brandRed,
    },
    [variants.standard]: {
      fg: gray4,
      bg: gray5,
      border: lighten(0.3, gray4),
    },
    [variants.highlight]: {
      fg: gray6,
      bg: gray7,
      border: gray8,
    },
    [variants.alert]: {
      fg: fullWhite,
      bg: alertRed,
      border: darken(0.2, alertRed),
    },
    [variants.success]: {
      fg: fullWhite,
      bg: successGreen,
      border: successGreen,
    },
    [variants.selectOn]: {
      fg: fullWhite,
      bg: gray4,
      border: lighten(0.3, gray4),
    },
    [variants.selectOff]: {
      fg: fullWhite,
      bg: gray9,
      border: lighten(0.3, gray9),
    },
    [variants.disabled]: {
      fg: lighten(0.5, gray4),
      bg: gray5,
      border: lighten(0.3, gray4),
    },
    [variants.dimmed]: {
      fg: lighten(0.2, gray4),
      bg: "rgba(63, 63, 63, 0.5)",
      border: lighten(0.3, gray4),
    },
    [variants.primary]: {
      fg: fullWhite,
      bg: lightBlue,
      border: lightBlue,
    },
    [variants.secondary]: {
      fg: fullWhite,
      bg: gray10,
      border: gray10,
    },
    [variants.ghost]: {
      fg: black,
      bg: gray5,
      border: gray5,
    },
    [variants.danger]: {
      fg: fullWhite,
      bg: alertRed,
      border: alertRed,
    },
    [variants.warning]: {
      fg: black,
      bg: yellow,
      border: yellow,
    },
  },
  borders: {
    size: { thin: "1px", thick: "3px" },
    width: {
      standard: "1px",
    },
    radius: {
      small: "0.313rem", // 5px
      standard: "0.375rem", // 6px
      medium: "0.625rem", // 10px
      large: "1rem",

      rounded: "50%",
    },
  },
  zIndex: {
    base: "auto",
    tooltip: 1,
    overlay: 2,
    background: -1,
  },
  breakpoints: {
    hypersmall: "360px",
    ultrasmall: "480px",
    xtrasmall: "640px",
    small: "769px",
    medium: "992px",
    large: "1100px",
    xtralarge: "1400px",
  },
  widgets: {},
}

const borderWidth = theme.borders.size.thin
const borderStyle = "solid"

const generateBorder = (color) => ({
  width: borderWidth,
  color,
  style: borderStyle,
  inline: `${borderWidth} ${borderStyle} ${color}`,
})
// generate borders based on colors
Object.keys(variants).forEach((variant) => {
  theme.borders[variant] = generateBorder(theme.colors[variant].border)
})
export const DefaultTheme = theme
